'use client';

import { useTranslation } from 'react-i18next';
import { 
    Sparkles, 
    PenTool, 
    ArrowRight, 
    Calendar, 
    MapPin,
    WandSparkles,
    Compass,
    Zap
} from 'lucide-react';
import { useLogin } from '@city-sage/aurum-avis-labs-components';
import { useIsAuthenticated } from '@azure/msal-react';
import { useGlobalChat } from '../chat/ChatProvider';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import CompactHeader from '../CompactHeader';

export const CreateItineraryPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate({ from: '/itineraries/create' });
    const isAuthenticated = useIsAuthenticated();
    const { promptLoginModal } = useLogin();
    
    // Chat sheet integration  
    const { open: openChat } = useGlobalChat();
    

    const handleManualCreate = () => {
        if (!isAuthenticated) {
            promptLoginModal({
                title: 'Sign in to create itinerary',
                description: 'Create an account or sign in to start building your personalized travel itinerary.'
            });
            return;
        }

        navigate({
            to: '/itineraries/create/manual'
        });
    };

    const handleAIAssistant = () => {
        openChat();
    };

    return (
        <div 
            className="flex flex-col bg-gray-50 overflow-hidden"
        >
            <CompactHeader 
                icon={Sparkles} 
                title={t('itinerary.create.title')} 
                subtitle={t('itinerary.create.subtitle')} 
            />
            <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
                {/* Creation Options */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="space-y-3"
                >
                    {/* AI-Powered Planning */}
                    <button 
                        onClick={handleAIAssistant}
                        className="w-full p-5 rounded-2xl border-2 transition-all duration-200 text-left border-gray-200 bg-white hover:border-primary/30 hover:bg-primary/5 active:scale-[0.98]"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <div className="h-6 w-6 rounded-md bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                                    <Sparkles className="h-3.5 w-3.5 text-white" />
                                </div>
                                <h3 className="font-semibold text-gray-900">{t('itinerary.create.aiAssistance.title')}</h3>
                            </div>
                            <ArrowRight className="h-5 w-5 text-gray-400" />
                        </div>
                        <p className="text-gray-600 text-sm mb-3">
                            {t('itinerary.create.aiAssistance.description')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <div className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                                <WandSparkles className="h-3 w-3" />
                                <span>{t('itinerary.create.aiAssistance.features.suggestions')}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                                <Calendar className="h-3 w-3" />
                                <span>{t('itinerary.create.aiAssistance.features.planning')}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                                <MapPin className="h-3 w-3" />
                                <span>{t('itinerary.create.aiAssistance.features.insights')}</span>
                            </div>
                        </div>
                    </button>

                    {/* Create From Scratch */}
                    <button 
                        onClick={handleManualCreate}
                        className="w-full p-5 rounded-2xl border-2 transition-all duration-200 text-left border-gray-200 bg-white hover:border-primary/30 hover:bg-primary/5 active:scale-[0.98]"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <div className="h-6 w-6 rounded-md bg-gray-100 flex items-center justify-center">
                                    <PenTool className="h-3.5 w-3.5 text-gray-700" />
                                </div>
                                <h3 className="font-semibold text-gray-900">
                                    {t('itinerary.create.manual.title')}
                                </h3>
                            </div>
                            <ArrowRight className="h-5 w-5 text-gray-400" />
                        </div>
                        <p className="text-gray-600 text-sm mb-3">
                            {t('itinerary.create.manual.description')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                <Compass className="h-3 w-3" />
                                <span>Full Control</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                <Zap className="h-3 w-3" />
                                <span>Quick Start</span>
                            </div>
                        </div>
                    </button>
                </motion.div>
            </div>
        </div>
    );
};